import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useEffect, useState, useRef, useMemo } from "react";
import { Toast } from "primereact/toast";
import orderServices from '../../services/orders/orderservice';
import { POSModel } from '../../models/orders/order-list-model';
import { Button } from "primereact/button";
import { useReactToPrint } from "react-to-print";
import { ProductSummaryProps, LineItem } from "../../models/Delivery-Executive/DeliveryExecutive";

const ProductSummary = ({ categorySummaries = [], lineItems = [], drivername = "", vesselDetails = [] }: ProductSummaryProps) => {
  const toast = useRef<Toast>(null);
  const [posData, setPOSData] = useState<POSModel | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `
      @page { 
        size: auto;
        margin: 10mm;
      }
      @media print {
        body { 
          -webkit-print-color-adjust: exact;
        }
        .p-datatable .p-datatable-thead > tr > th {
          background-color: #f8f9fa !important;
        }
        .font-bold {
          font-weight: bold !important;
        }
        .text-blue-600 {
          color: #2563eb !important;
        }
        .bg-blue-50 {
          background-color: #eff6ff !important;
        }/* Remove headers and footers */
        @page { margin: 0.5cm; }
        body { margin: 1.6cm; }
      }
    `
  });

  const formatDate = (dateString: string) => {
    const date = dateString ? new Date(dateString) : new Date();
    if (isNaN(date.getTime())) {
      return new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  useEffect(() => {
    const getALLPOS = async () => {
      try {
        const response = await orderServices.getPOS();
        setPOSData(response[0]);
      } catch (error) {
        console.error("Error fetching POS:", error);
      }
    };
    getALLPOS();
  }, []);

  const categoryRowGroupHeader = (rowData: LineItem) => {
    return <strong>{rowData.categoryName}</strong>;
  };

  const categoryRowGroupFooter = (rowData: LineItem) => {
    const category = categorySummaries.find(c => c.categoryId === rowData.categoryId);

    return (
      <div style={{ fontWeight: 'bold' }}>
        {rowData.categoryName} Total: {category?.totalQuantity || 0} items (₹{(category?.totalGrossAmount || 0).toFixed(2)})
      </div>
    );
  };

  const getChallanNumber = () => {
    if (lineItems.length === 0) return 'Not available';
    const validItems = lineItems.filter(item => item.challanNo);
    if (validItems.length === 0) return 'Not available';
    return validItems[0].challanNo;
  };

  // ✅ Ignore negative grossAmount items
  const validLineItems = useMemo<LineItem[]>(
    () => lineItems.filter(item => Number(item.grossAmount) >= 0),
    [lineItems]
  );

  const validCategorySummaries = useMemo<any[]>(
    () => categorySummaries.filter(cat => Number(cat.totalGrossAmount) >= 0),
    [categorySummaries]
  );

  const gstSummary = useMemo(() => {
    const grouped = new Map<number, {
      gst: number;
      qty: number;
      valueWithoutTax: number;
      taxAmount: number;
      totalAmount: number;
    }>();

    validLineItems.forEach((item) => {
      const gstRate = Number(item.gst ?? 0);
      const entry = grouped.get(gstRate) ?? {
        gst: gstRate,
        qty: 0,
        valueWithoutTax: 0,
        taxAmount: 0,
        totalAmount: 0,
      };

      entry.qty += Number(item.quantity || 0);
      entry.valueWithoutTax += Number(item.taxableAmount || 0);
      entry.taxAmount += Number(item.taxAmount || 0);
      entry.totalAmount += Number(item.grossAmount || 0);
      grouped.set(gstRate, entry);
    });

    return Array.from(grouped.values()).sort((a, b) => a.gst - b.gst);
  }, [validLineItems]);

  // ✅ Recalculate totals using only valid (non-negative) data
  const priceValueTotal = validCategorySummaries.reduce((sum, cat) => sum + (cat.totalTaxableAmount || 0), 0);
  const taxValueTotal = validCategorySummaries.reduce((sum, cat) => sum + (cat.totalTaxAmount || 0), 0);
  const grandTotal = validCategorySummaries.reduce((sum, cat) => sum + (cat.totalGrossAmount || 0), 0);
  
  return (
    <div className="card p-4">
      <Toast ref={toast} />
      <div className="flex align-items-center justify-content-between mb-4">
        <h3 className="mb-0">Delivery Challan Summary</h3>
        <Button 
          icon="pi pi-print" 
          label="Print Challan" 
          onClick={handlePrint}
          className="p-button"
        />
      </div>

      <div ref={printRef}>
        {/* Address sections */}
        <div className="grid mb-4">
          <div className="col-6">
            <div className="p-3 border-round border-1 surface-border">
              <h4>From:</h4>
              <p>Desiri Naturals Private Limited</p>
              {posData ? (
                <>
                  <p>{posData.address1}</p>
                  {posData.address2 && <p>{posData.address2}</p>}
                  {posData.landmark && <p>{posData.landmark}</p>}
                  <p>{posData.cityName}, {posData.stateName} - {posData.zipcode}</p>
                  <p>GST #: {'29AAGCD5864J1Z4'}</p>
                </>
              ) : (
                <p>Address not available</p>
              )}
            </div>
          </div>
          <div className="col-6">
            <div className="p-3 border-round border-1 surface-border">
              <h4>To:</h4>
              <p>Un Registered Persons (URP)/ Retail Customers</p>
              <p>Retail customers List as below</p>
              <p>List of Products: As mentioned below</p>
            </div>
          </div>
        </div>

        <div className="mb-4 p-3 border-round border-1 surface-border">
          <div className="grid">
            <h4>Purpose of material movement:</h4>
            <ol>
              <li>Below list of materials are moved against the customer orders placed on Desiri Naturals Pvt Ltd through online.</li>
              <li>Invoice will be generated after physical delivery to customer address mentioned below.</li>
            </ol>
          </div>
        </div>

       <div className="mb-4 p-3 border-round border-1 surface-border">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-1">
              <strong>Delivery Challan # :</strong> {getChallanNumber()}
            </div>
            <div className="col-span-1 text-right">
              <strong>Eway Bill # :</strong> {validLineItems[0]?.ewayBillNo || '-'}
            </div>
            <div className="col-span-1 text-right">
              <strong>Vehicle # :</strong> {validLineItems[0]?.vehicleNo || '-'}
            </div>

            <div className="col-span-1">
              <strong>Generated By :</strong> {drivername || '-'}
            </div>
            <div className="col-span-1 text-right">
              <strong>Generated Date :</strong> {validLineItems.length > 0 && validLineItems[0].createdDate
                ? formatDate(validLineItems[0].createdDate)
                : formatDate('')}
            </div>
          </div>
        </div>

        
        <div className="mb-4">
          <h5>Product Details</h5>
          {validLineItems.length > 0 ? (
            <DataTable
              value={validLineItems}
              rowGroupMode="subheader"
              groupRowsBy="categoryName"
              sortMode="single"
              sortField="categoryName"
              rowGroupHeaderTemplate={categoryRowGroupHeader}
              rowGroupFooterTemplate={categoryRowGroupFooter}
              stripedRows
              showGridlines
              className="p-datatable-sm"
            >
              <Column field="productName" header="Product" style={{ minWidth: '20rem', textAlign: 'left' }} />
              <Column field="quantity" header="Quantity" style={{ width: '5rem', textAlign: 'center' }} />
              <Column field="hsnCode" header="HSN" style={{ width: '8rem', textAlign: 'center' }} />
              <Column
                header="GST %"
                style={{ width: '7rem', textAlign: 'center' }}
                body={(rowData) => `${Number(rowData.gst ?? 0).toFixed(2)}%`}
              />
              <Column
                header="Price Value(₹)"
                style={{ width: '10rem', textAlign: 'center' }}
                body={(rowData) => (rowData.taxableAmount || 0).toFixed(2)}
              />
              <Column
                field="taxAmount"
                header="Tax Value(₹)"
                style={{ width: '10rem', textAlign: 'center' }}
                body={(rowData) => (rowData.taxAmount || 0).toFixed(2)}
              />
              <Column
                field="grossAmount"
                header="Total Amount(₹)"
                style={{ width: '10rem', textAlign: 'center' }}
                body={(rowData) => (rowData.grossAmount || 0).toFixed(2)}
              />
            </DataTable>
          ) : (
            <div className="p-4 text-center">No products found in the challan.</div>
          )}
        </div>

        <div className="mb-4 p-3 border-round border-1 surface-border">
          <h4>GST Summary</h4>
          {gstSummary.length > 0 ? (
            <DataTable
              value={[
                ...gstSummary,
                {
                  gst: 999,
                  qty: gstSummary.reduce((sum, item) => sum + (item.qty || 0), 0),
                  valueWithoutTax: gstSummary.reduce((sum, item) => sum + (item.valueWithoutTax || 0), 0),
                  taxAmount: gstSummary.reduce((sum, item) => sum + (item.taxAmount || 0), 0),
                  totalAmount: gstSummary.reduce((sum, item) => sum + (item.totalAmount || 0), 0),
                },
              ]}
              className="p-datatable-sm"
              showGridlines
              stripedRows
              rowClassName={(rowData) => rowData.gst === 999 ? 'font-bold bg-blue-50' : ''}
            >
              <Column
                header="GST %"
                body={(rowData) => rowData.gst === 999 ? 'Total' : `${Number(rowData.gst || 0).toFixed(2)}%`}
                style={{ width: '8rem' }}
              />
              <Column field="qty" header="Qty" style={{ textAlign: 'center' }} />
              <Column header="Value without Tax (₹)" body={(rowData) => (rowData.valueWithoutTax || 0).toFixed(2)} style={{ textAlign: 'right' }} />
              <Column header="Tax Amount (₹)" body={(rowData) => (rowData.taxAmount || 0).toFixed(2)} style={{ textAlign: 'right' }} />
              <Column header="Total Amount (₹)" body={(rowData) => (rowData.totalAmount || 0).toFixed(2)} style={{ textAlign: 'right' }} />
            </DataTable>
          ) : (
            <div className="p-4 text-center">No GST breakdown available.</div>
          )}
        </div>

        <div className="p-3 border-round border-1 surface-border">
          <div className="grid">
            <div className="col-12">
              <div className="text-left">
                <h4>Summary Totals</h4>
                <DataTable
                  value={[...validCategorySummaries, {
                    categoryName: 'Total',
                    totalTaxableAmount: priceValueTotal,
                    totalTaxAmount: taxValueTotal,
                    totalGrossAmount: grandTotal
                  }]}
                  className="p-datatable-sm"
                  showGridlines
                  stripedRows
                  rowClassName={(rowData) => rowData.categoryName === 'Total' ? 'font-bold bg-blue-50' : ''}
                >
                  <Column
                    field="categoryName"
                    header="Category"
                    style={{ minWidth: '200px' }}
                    body={(rowData) => (
                      <strong className={rowData.categoryName === 'Total' ? 'text-blue-600' : ''}>
                        {rowData.categoryName}
                      </strong>
                    )}
                  />
                  <Column
                    header="Total Price Value (₹)"
                    body={(rowData) => (rowData.totalTaxableAmount || 0).toFixed(2)}
                    style={{ textAlign: 'right' }}
                  />
                  <Column
                    header="Total Tax Value (₹)"
                    body={(rowData) => (rowData.totalTaxAmount || 0).toFixed(2)}
                    style={{ textAlign: 'right' }}
                  />
                  <Column
                    header="Total Amount (₹)"
                    body={(rowData) => (rowData.totalGrossAmount || 0).toFixed(2)}
                    style={{ textAlign: 'right' }}
                  />
                </DataTable>
              </div>
            </div>
          </div>
        </div>

        {/* Vessel Details Table */}
        <div className="p-3 border-round border-1 surface-border mt-4">
          <div className="grid">
            <div className="col-12">
              <div className="text-left">
                <h4>Vessel Details</h4>
                {vesselDetails.length > 0 ? (
                  <DataTable
                    value={vesselDetails}
                    className="p-datatable-sm"
                    showGridlines
                    stripedRows
                  >
                    <Column field="productName" header="Product Name" style={{ minWidth: '200px' }} />
                    <Column field="totalQuantity" header="Total Quantity" style={{ width: '120px', textAlign: 'center' }} />
                    <Column field="vessels_5L" header="5L Vessels" style={{ width: '100px', textAlign: 'center' }} />
                    <Column field="vessels_3L" header="3L Vessels" style={{ width: '100px', textAlign: 'center' }} />
                    <Column field="vessels_2L" header="2L Vessels" style={{ width: '100px', textAlign: 'center' }} />
                  </DataTable>
                ) : (
                  <div className="p-4 text-center">No vessel details available.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSummary;